import React, { useState } from 'react';
import { FiPlus, FiTrash2, FiPlay, FiSquare, FiUpload, FiEye, FiEyeOff } from 'react-icons/fi';
import { adminAPI } from '../../../../services/api';
import ttsService from '../../../../utils/textToSpeech';

const DialogBlockEditor = ({ block, onUpdate, availableVoices = [], setError, setSuccess }) => {
    const [messages, setMessages] = useState(block.messages || []);
    const [vocabulary, setVocabulary] = useState(block.vocabulary || []);
    const [showVocabulary, setShowVocabulary] = useState(false);
    const [isPlayingIndex, setIsPlayingIndex] = useState(null);
    const [isPlayingVocabIndex, setIsPlayingVocabIndex] = useState(null);

    const handleMessagesChange = (updatedMessages) => {
        setMessages(updatedMessages);
        onUpdate({ messages: updatedMessages, vocabulary });
    };

    const handleVocabularyChange = (updatedVocabulary) => {
        setVocabulary(updatedVocabulary);
        onUpdate({ messages, vocabulary: updatedVocabulary });
    };

    const addMessage = () => {
        const updated = [...messages, {
            speaker: '',
            messageEn: '',
            messageRu: '',
            voiceId: 'en-US-JennyNeural',
            audioType: 'auto',
            audioUrl: ''
        }];
        handleMessagesChange(updated);
    };

    const addVocabItem = () => {
        const updated = [...vocabulary, {
            en: '',
            ru: '',
            voiceId: 'en-US-JennyNeural',
            audioType: 'auto',
            audioUrl: ''
        }];
        handleVocabularyChange(updated);
    };

    const updateMessage = (index, field, value) => {
        const updated = [...messages];
        updated[index][field] = value;
        handleMessagesChange(updated);
    };

    const updateVocabItem = (index, field, value) => {
        const updated = [...vocabulary];
        updated[index][field] = value;
        handleVocabularyChange(updated);
    };

    const removeMessage = (index) => {
        handleMessagesChange(messages.filter((_, i) => i !== index));
    };

    const removeVocabItem = (index) => {
        handleVocabularyChange(vocabulary.filter((_, i) => i !== index));
    };

    const playMessage = (text, voiceId, index) => {
        if (!text) {
            alert('Введите текст сообщения');
            return;
        }

        setIsPlayingIndex(index);

        ttsService.speak(text, {
            rate: 1,
            lang: 'en-US',
            voice: voiceId || 'en-US-JennyNeural'
        }).then(() => {
            setIsPlayingIndex(null);
        }).catch(error => {
            console.error('Ошибка воспроизведения:', error);
            setError('Ошибка воспроизведения');
            setIsPlayingIndex(null);
        });
    };

    const stopAudio = () => {
        ttsService.cancel();
        setIsPlayingIndex(null);
        setIsPlayingVocabIndex(null);
    };

    const handleAudioUpload = async (index, file, isVocab = false) => {
        if (!file || !file.type.startsWith('audio/')) {
            setError('Выберите аудио файл');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setError('Размер файла не должен превышать 10MB');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('audio', file);

            const response = await adminAPI.uploadSentenceAudio(formData);

            if (isVocab) {
                const updated = [...vocabulary];
                updated[index].audioUrl = response.data.audioUrl;
                updated[index].audioType = 'custom';
                handleVocabularyChange(updated);
            } else {
                const updated = [...messages];
                updated[index].audioUrl = response.data.audioUrl;
                updated[index].audioType = 'custom';
                handleMessagesChange(updated);
            }

            setSuccess('Аудио успешно загружено');
        } catch (error) {
            console.error('Ошибка загрузки:', error);
            setError('Ошибка при загрузке аудио');
        }
    };

    return (
        <div>
            {/* Кнопка для показа/скрытия вводной лексики */}
            <div style={{ marginBottom: '20px' }}>
                <button
                    type="button"
                    onClick={() => setShowVocabulary(!showVocabulary)}
                    style={{
                        padding: '10px 16px',
                        background: showVocabulary ? '#52c41a' : '#4a90e2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    {showVocabulary ? <FiEyeOff /> : <FiEye />}
                    {showVocabulary ? 'Скрыть вводную лексику' : 'Показать вводную лексику'}
                    {vocabulary.length > 0 && (
                        <span style={{
                            background: 'rgba(255,255,255,0.3)',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px'
                        }}>
                            {vocabulary.length}
                        </span>
                    )}
                </button>
            </div>

            {/* Секция вводной лексики */}
            {showVocabulary && (
                <div style={{
                    marginBottom: '30px',
                    padding: '20px',
                    background: '#f0f9ff',
                    borderRadius: '8px',
                    border: '2px solid #bae7ff'
                }}>
                    <div style={{ marginBottom: '15px' }}>
                        <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', color: '#1890ff' }}>
                            Вводная лексика
                        </h3>
                        <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>
                            Слова и фразы для изучения перед диалогом
                        </p>
                    </div>

                    {(!vocabulary || vocabulary.length === 0) ? (
                        <div style={{
                            padding: '30px',
                            textAlign: 'center',
                            background: 'white',
                            borderRadius: '8px',
                            border: '1px dashed #d9d9d9',
                            color: '#999'
                        }}>
                            Нет вводной лексики. Нажмите "Добавить слово/фразу" чтобы начать.
                        </div>
                    ) : (
                        vocabulary.map((item, index) => (
                            <div
                                key={index}
                                style={{
                                    padding: '15px',
                                    background: 'white',
                                    borderRadius: '8px',
                                    marginBottom: '12px',
                                    border: '1px solid #d9ecff'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                    <span style={{ fontWeight: '600', color: '#1890ff' }}>
                                        Фраза {index + 1}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeVocabItem(index)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#ff4d4f',
                                            cursor: 'pointer',
                                            fontSize: '18px'
                                        }}
                                        title="Удалить"
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>

                                <div style={{ display: 'grid', gap: '12px' }}>
                                    <div>
                                        <label className="form-label" style={{ fontSize: '13px' }}>
                                            Английский текст
                                        </label>
                                        <textarea
                                            className="form-input"
                                            placeholder="Nice to meet you"
                                            value={item.en}
                                            onChange={(e) => updateVocabItem(index, 'en', e.target.value)}
                                            rows={2}
                                        />
                                    </div>

                                    <div>
                                        <label className="form-label" style={{ fontSize: '13px' }}>
                                            Перевод на русский
                                        </label>
                                        <textarea
                                            className="form-input"
                                            placeholder="Приятно познакомиться"
                                            value={item.ru}
                                            onChange={(e) => updateVocabItem(index, 'ru', e.target.value)}
                                            rows={2}
                                        />
                                    </div>

                                    <div>
                                        <label className="form-label" style={{ fontSize: '13px' }}>
                                            Озвучка
                                        </label>
                                        <div style={{ marginBottom: '12px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name={`vocab-audio-type-${index}`}
                                                    checked={item.audioType === 'auto'}
                                                    onChange={() => updateVocabItem(index, 'audioType', 'auto')}
                                                />
                                                <span style={{ fontSize: '13px' }}>Автоматическая озвучка (TTS)</span>
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name={`vocab-audio-type-${index}`}
                                                    checked={item.audioType === 'custom'}
                                                    onChange={() => updateVocabItem(index, 'audioType', 'custom')}
                                                />
                                                <span style={{ fontSize: '13px' }}>Загрузить свое аудио</span>
                                            </label>
                                        </div>

                                        {item.audioType === 'auto' ? (
                                            <div>
                                                <label className="form-label" style={{ fontSize: '12px', marginBottom: '6px' }}>
                                                    Выбор голоса
                                                </label>
                                                <select
                                                    className="form-input"
                                                    value={item.voiceId || 'en-US-JennyNeural'}
                                                    onChange={(e) => updateVocabItem(index, 'voiceId', e.target.value)}
                                                >
                                                    {availableVoices && availableVoices.length > 0 ? (
                                                        availableVoices.filter(v => v.ShortName && v.DisplayName).map(voice => (
                                                            <option key={voice.ShortName} value={voice.ShortName}>
                                                                {voice.DisplayName}
                                                            </option>
                                                        ))
                                                    ) : (
                                                        <option value="en-US-JennyNeural">Jenny (США, женский)</option>
                                                    )}
                                                </select>
                                            </div>
                                        ) : (
                                            <div>
                                                <input
                                                    type="file"
                                                    accept="audio/*"
                                                    onChange={(e) => handleAudioUpload(index, e.target.files[0], true)}
                                                    style={{ display: 'none' }}
                                                    id={`vocab-audio-upload-${block.id}-${index}`}
                                                />
                                                <label
                                                    htmlFor={`vocab-audio-upload-${block.id}-${index}`}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        padding: '10px 16px',
                                                        background: item.audioUrl ? '#52c41a' : '#4a90e2',
                                                        color: 'white',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    <FiUpload /> {item.audioUrl ? '✓ Аудио загружено' : 'Загрузить аудио файл'}
                                                </label>
                                                {item.audioUrl && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            updateVocabItem(index, 'audioUrl', '');
                                                        }}
                                                        style={{
                                                            marginLeft: '10px',
                                                            padding: '10px 16px',
                                                            background: 'transparent',
                                                            color: '#ff4d4f',
                                                            border: '1px solid #ff4d4f',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            fontSize: '13px'
                                                        }}
                                                    >
                                                        Удалить
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (isPlayingVocabIndex === index) {
                                                    stopAudio();
                                                } else {
                                                    setIsPlayingVocabIndex(index);
                                                    ttsService.speak(item.en, {
                                                        rate: 1,
                                                        lang: 'en-US',
                                                        voice: item.voiceId || 'en-US-JennyNeural'
                                                    }).then(() => {
                                                        setIsPlayingVocabIndex(null);
                                                    }).catch(error => {
                                                        console.error('Ошибка воспроизведения:', error);
                                                        setError('Ошибка воспроизведения');
                                                        setIsPlayingVocabIndex(null);
                                                    });
                                                }
                                            }}
                                            disabled={!item.en}
                                            style={{
                                                padding: '8px 16px',
                                                background: isPlayingVocabIndex === index ? '#ff4d4f' : '#4a90e2',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: item.en ? 'pointer' : 'not-allowed',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                fontSize: '13px'
                                            }}
                                        >
                                            {isPlayingVocabIndex === index ? <><FiSquare /> Стоп</> : <><FiPlay /> Прослушать</>}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    <button
                        type="button"
                        onClick={addVocabItem}
                        style={{
                            width: '100%',
                            padding: '12px',
                            background: 'white',
                            border: '2px dashed #91d5ff',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: '#1890ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.borderColor = '#1890ff';
                            e.target.style.background = '#e6f7ff';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.borderColor = '#91d5ff';
                            e.target.style.background = 'white';
                        }}
                    >
                        <FiPlus /> Добавить слово/фразу
                    </button>
                </div>
            )}

            <div style={{ marginBottom: '15px' }}>
                <label className="form-label">Диалог</label>
                <p style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                    Добавьте реплики диалога с указанием говорящего
                </p>
            </div>

            {(!messages || messages.length === 0) ? (
                <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    background: '#fafafa',
                    borderRadius: '8px',
                    border: '1px dashed #d9d9d9',
                    color: '#999'
                }}>
                    Нет сообщений в диалоге. Нажмите "Добавить реплику" чтобы начать.
                </div>
            ) : (
                messages.map((message, index) => (
                <div
                    key={index}
                    style={{
                        padding: '15px',
                        background: '#f9f9f9',
                        borderRadius: '8px',
                        marginBottom: '12px',
                        border: '1px solid #e0e0e0'
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <span style={{ fontWeight: '600', color: '#666' }}>
                            Реплика {index + 1}
                        </span>
                        <button
                            type="button"
                            onClick={() => removeMessage(index)}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#ff4d4f',
                                cursor: 'pointer',
                                fontSize: '18px'
                            }}
                            title="Удалить"
                        >
                            <FiTrash2 />
                        </button>
                    </div>

                    <div style={{ display: 'grid', gap: '12px' }}>
                        <div>
                            <label className="form-label" style={{ fontSize: '13px' }}>
                                Говорящий
                            </label>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Например: Житель Барселоны, Москвич"
                                value={message.speaker}
                                onChange={(e) => updateMessage(index, 'speaker', e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="form-label" style={{ fontSize: '13px' }}>
                                Текст реплики (английский)
                            </label>
                            <textarea
                                className="form-input"
                                placeholder="Excuse me, where are you from?"
                                value={message.messageEn}
                                onChange={(e) => updateMessage(index, 'messageEn', e.target.value)}
                                rows={2}
                            />
                        </div>

                        <div>
                            <label className="form-label" style={{ fontSize: '13px' }}>
                                Перевод на русский
                            </label>
                            <textarea
                                className="form-input"
                                placeholder="Извините, откуда вы?"
                                value={message.messageRu}
                                onChange={(e) => updateMessage(index, 'messageRu', e.target.value)}
                                rows={2}
                            />
                        </div>

                        <div>
                            <label className="form-label" style={{ fontSize: '13px' }}>
                                Озвучка
                            </label>
                            <div style={{ marginBottom: '12px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name={`audio-type-${index}`}
                                        checked={message.audioType === 'auto'}
                                        onChange={() => updateMessage(index, 'audioType', 'auto')}
                                    />
                                    <span style={{ fontSize: '13px' }}>Автоматическая озвучка (TTS)</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name={`audio-type-${index}`}
                                        checked={message.audioType === 'custom'}
                                        onChange={() => updateMessage(index, 'audioType', 'custom')}
                                    />
                                    <span style={{ fontSize: '13px' }}>Загрузить свое аудио</span>
                                </label>
                            </div>

                            {message.audioType === 'auto' ? (
                                <div>
                                    <label className="form-label" style={{ fontSize: '12px', marginBottom: '6px' }}>
                                        Выбор голоса
                                    </label>
                                    <select
                                        className="form-input"
                                        value={message.voiceId || 'en-US-JennyNeural'}
                                        onChange={(e) => updateMessage(index, 'voiceId', e.target.value)}
                                    >
                                        {availableVoices && availableVoices.length > 0 ? (
                                            availableVoices.filter(v => v.ShortName && v.DisplayName).map(voice => (
                                                <option key={voice.ShortName} value={voice.ShortName}>
                                                    {voice.DisplayName}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="en-US-JennyNeural">Jenny (США, женский)</option>
                                        )}
                                    </select>
                                </div>
                            ) : (
                                <div>
                                    <input
                                        type="file"
                                        accept="audio/*"
                                        onChange={(e) => handleAudioUpload(index, e.target.files[0])}
                                        style={{ display: 'none' }}
                                        id={`audio-upload-${block.id}-${index}`}
                                    />
                                    <label
                                        htmlFor={`audio-upload-${block.id}-${index}`}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '10px 16px',
                                            background: message.audioUrl ? '#52c41a' : '#4a90e2',
                                            color: 'white',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        <FiUpload /> {message.audioUrl ? '✓ Аудио загружено' : 'Загрузить аудио файл'}
                                    </label>
                                    {message.audioUrl && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                updateMessage(index, 'audioUrl', '');
                                            }}
                                            style={{
                                                marginLeft: '10px',
                                                padding: '10px 16px',
                                                background: 'transparent',
                                                color: '#ff4d4f',
                                                border: '1px solid #ff4d4f',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '13px'
                                            }}
                                        >
                                            Удалить
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    if (isPlayingIndex === index) {
                                        stopAudio();
                                    } else {
                                        playMessage(message.messageEn, message.voiceId, index);
                                    }
                                }}
                                disabled={!message.messageEn}
                                style={{
                                    padding: '8px 16px',
                                    background: isPlayingIndex === index ? '#ff4d4f' : '#4a90e2',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: message.messageEn ? 'pointer' : 'not-allowed',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '13px'
                                }}
                            >
                                {isPlayingIndex === index ? <><FiSquare /> Стоп</> : <><FiPlay /> Прослушать</>}
                            </button>
                        </div>
                    </div>
                </div>
            )))}

            <button
                type="button"
                onClick={addMessage}
                style={{
                    width: '100%',
                    padding: '12px',
                    background: 'white',
                    border: '2px dashed #d9d9d9',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px'
                }}
                onMouseEnter={(e) => {
                    e.target.style.borderColor = '#4a90e2';
                    e.target.style.color = '#4a90e2';
                }}
                onMouseLeave={(e) => {
                    e.target.style.borderColor = '#d9d9d9';
                    e.target.style.color = '#666';
                }}
            >
                <FiPlus /> Добавить реплику
            </button>
        </div>
    );
};

export default DialogBlockEditor;
